import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useLang } from "../context/LangContext";
import { pickLang, t } from "../lib/i18n";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";

const CATS = ["all", "speakers", "amplifiers", "mixers", "microphones", "lighting", "stage", "video", "cctv"];

export default function Products() {
  const { lang } = useLang();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const initialCat = searchParams.get("category");
  const [cat, setCat] = useState(initialCat && CATS.includes(initialCat) ? initialCat : "all");
  const [search, setSearch] = useState("");
  const brandFilter = (searchParams.get("brand") || "").trim();

  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)); }, []);

  const setCategory = (c) => {
    setCat(c);
    const next = {};
    if (c !== "all") next.category = c;
    if (brandFilter) next.brand = brandFilter;
    setSearchParams(next);
  };

  const clearBrand = () => {
    const next = {};
    if (cat !== "all") next.category = cat;
    setSearchParams(next);
  };

  const filtered = useMemo(() => {
    let arr = products;
    if (cat !== "all") arr = arr.filter((p) => p.category === cat);
    if (brandFilter) {
      const bf = brandFilter.toLowerCase();
      arr = arr.filter((p) => (p.brand || "").toLowerCase().includes(bf));
    }
    if (search) {
      const q = search.toLowerCase();
      arr = arr.filter((p) =>
        pickLang(p.name, lang).toLowerCase().includes(q) ||
        (p.brand || "").toLowerCase().includes(q) ||
        (p.sku || "").toLowerCase().includes(q)
      );
    }
    return arr;
  }, [products, cat, search, lang, brandFilter]);

  return (
    <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20" data-testid="products-page">
      <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
        <div>
          <div className="label-tag mb-4 slash">{t("sections.products_eyebrow", lang)}</div>
          <h1 className="display-font text-5xl sm:text-7xl font-black uppercase leading-[0.9] tracking-tighter">
            {lang === "gr" ? <>Ο <span className="neon-text">εξοπλισμός.</span></> : <>The <span className="neon-text">arsenal.</span></>}
          </h1>
        </div>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={lang === "gr" ? "Αναζήτηση..." : "Search gear..."}
          className="input-dark max-w-xs"
          data-testid="product-search"
        />
      </div>

      {/* Category filters */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="category-filters">
        {CATS.map((c) => (
          <button
            key={c}
            onClick={() => setCategory(c)}
            data-testid={`cat-${c}`}
            className={`mono-font text-xs uppercase tracking-[0.2em] px-5 py-3 border transition-all ${
              cat === c ? "bg-[#ccff00] text-black border-[#ccff00]" : "border-[#2a2a35] text-gray-300 hover:border-[#ccff00] hover:text-[#ccff00]"
            }`}
          >
            {t(`categories.${c}`, lang)}
          </button>
        ))}
      </div>

      {brandFilter && (
        <div className="mb-12" data-testid="brand-filter-chip">
          <button
            onClick={clearBrand}
            className="inline-flex items-center gap-2 mono-font text-[11px] uppercase tracking-[0.2em] px-4 py-2 border border-[#ccff00] bg-[#ccff00]/10 text-[#ccff00] hover:bg-[#ccff00]/20"
          >
            {lang === "gr" ? "Brand:" : "Brand:"} <span className="font-bold">{brandFilter}</span>
            <span className="text-base leading-none">×</span>
          </button>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="text-gray-500 py-16 text-center mono-font">{lang === "gr" ? "Καμία εγγραφή." : "No matches."}</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filtered.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      )}
    </div>
  );
}
